import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { parseGroupScheduleData } from '@/utils/groupScheduleParser';
import { effectiveCourtHidden } from '@/lib/courtVisibility';

export interface GroupParticipant {
  id: string;
  letter: string; // A, B, C
  player1: string;
  player2?: string;
  isUser: boolean;
}

export interface GroupMatch {
  id: string;
  matchId: string; // G-Masculino 1era-A-AB
  participants: [string, string]; // ["A", "B"]
  time: string; // "18:00"
  date: string; // "2025-10-24"
  court?: string;
}

export interface GroupInfo {
  groupName: string; // "Grupo A"
  category: string; // "Masculino 1era"
  subcategory?: string; // "A", "B"
  date: string; // "2025-10-24"
  startTime: string; // "18:00"
  endTime: string; // "21:00"
  court?: string; // "1"
  courtHidden?: boolean; // organizador ocultó la cancha preasignada al participante
  participants: GroupParticipant[];
  matches: GroupMatch[];
}

export function useUserGroupInfo(eventId: string) {
  const { user } = useAuth();
  const [groupInfo, setGroupInfo] = useState<GroupInfo | null>(null);
  const [allGroupInfos, setAllGroupInfos] = useState<GroupInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const userGroupsRef = useRef<Set<string>>(new Set());
  const lastScheduleSnapshotRef = useRef<string>('');

  const normalizeKey = (group?: string | null, category?: string | null) => {
    const g = (group || '').trim().replace(/\s+/g, ' ').toLowerCase();
    const c = (category || '').trim().replace(/\s+/g, ' ').toLowerCase();
    return `${g}|${c}`;
  };

  const processRegistration = async (
    userRegistration: any,
    userId: string
  ): Promise<GroupInfo | null> => {
    if (!userRegistration || !userRegistration.group) {
      return null;
    }

    const userGroup = (userRegistration.group?.trim() || '').replace(/\s+/g, ' ');
    const userCategory = (userRegistration.category?.trim() || '').replace(/\s+/g, ' ');
    const userSubcategory = userRegistration.subcategory?.trim() || null;

    const fullCategory = userSubcategory 
      ? `${userCategory} ${userSubcategory}`
      : userCategory;

    console.log('[useUserGroupInfo] Processing registration:', {
      group: userGroup,
      category: fullCategory
    });

    // Get all participants in the same group + category + subcategory
    let groupRegQuery = supabase
      .from('registrations')
      .select(`id, user_id, partner_user_id, player1_name, player2_name`)
      .eq('event_id', eventId)
      .eq('group', userGroup)
      .eq('status', 'confirmed');
    
    if (userCategory) {
      groupRegQuery = groupRegQuery.eq('category', userCategory);
    }
    if (userSubcategory) {
      groupRegQuery = groupRegQuery.eq('subcategory', userSubcategory);
    } else {
      groupRegQuery = groupRegQuery.is('subcategory', null);
    }
    
    const { data: groupRegistrations, error: groupError } = await groupRegQuery
      .order('registered_at', { ascending: true });

    if (groupError) throw groupError;

    // Get group calendar data
    let { data: groupCalendars, error: calendarError } = await supabase
      .from('group_calendars' as any)
      .select('*')
      .eq('tournament_id', eventId)
      .eq('group_name', userGroup)
      .eq('category', fullCategory)
      .order('created_at', { ascending: true });

    if ((!groupCalendars || groupCalendars.length === 0) && !calendarError) {
      const { data: calendarsIlike, error: errorIlike } = await supabase
        .from('group_calendars' as any)
        .select('*')
        .eq('tournament_id', eventId)
        .ilike('group_name', userGroup)
        .ilike('category', fullCategory)
        .order('created_at', { ascending: true });
      
      groupCalendars = calendarsIlike;
      calendarError = errorIlike;
    }

    if (calendarError) throw calendarError;

    const groupCalendar = groupCalendars && groupCalendars.length > 0 ? groupCalendars[0] : null;

    if (!groupCalendar) {
      // Try to find ANY group_calendar for the same group+category (any subcategory)
      // so we can use its team ordering (letters A/B/C aligned with the actual schedule)
      let teamsOrder: any[] | null = null;
      try {
        const { data: anyCalendars } = await supabase
          .from('group_calendars' as any)
          .select('teams, schedule_matrix')
          .eq('tournament_id', eventId)
          .ilike('group_name', userGroup)
          .ilike('category', fullCategory);
        if (anyCalendars && anyCalendars.length > 0) {
          const c: any = anyCalendars[0];
          teamsOrder = (c.teams && c.teams.length > 0) ? c.teams : (c.schedule_matrix?.teams || null);
        }
      } catch (e) {
        console.warn('[useUserGroupInfo] Could not fetch fallback teams order:', e);
      }

      // Helper: normalize team name for comparison
      const normName = (s: string) =>
        (s || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .replace(/\s*\/\s*/g, ' / ').replace(/\s+/g, ' ');

      // Build participants list. Prefer group_calendars.teams order so the A/B/C letters
      // line up with the actual schedule matrix the organizer uses.
      let participants: GroupParticipant[];
      if (teamsOrder && teamsOrder.length > 0) {
        const sortedTeams = [...teamsOrder].sort(
          (a: any, b: any) => (a.teamOrder || 0) - (b.teamOrder || 0)
        );
        participants = sortedTeams.map((team: any) => {
          // Find matching registration: first by registrationId, then by name
          let matchingReg = (groupRegistrations || []).find(
            (r: any) => r.id === team.registrationId
          );
          if (!matchingReg && team.name) {
            const teamFull = normName(team.name);
            matchingReg = (groupRegistrations || []).find((r: any) => {
              const regFull = normName(`${r.player1_name || ''}${r.player2_name ? ' / ' + r.player2_name : ''}`);
              return regFull === teamFull;
            });
          }
          const teamNames = team.name ? team.name.split(' / ') : [];
          return {
            id: matchingReg?.id || team.registrationId || `team-${team.teamOrder}`,
            letter: String.fromCharCode(65 + (team.teamOrder || 1) - 1),
            player1: matchingReg?.player1_name || teamNames[0]?.trim() || 'Por asignar',
            player2: matchingReg?.player2_name || teamNames[1]?.trim() || undefined,
            isUser: matchingReg
              ? (matchingReg.user_id === userId || matchingReg.partner_user_id === userId)
              : false
          };
        });
        console.log('[useUserGroupInfo] Fallback: built participants from group_calendars teams order', {
          group: userGroup, category: fullCategory,
          participants: participants.map(p => ({ letter: p.letter, name: `${p.player1}${p.player2 ? '/' + p.player2 : ''}`, isUser: p.isUser }))
        });
      } else {
        // Last resort: registration order
        participants = (groupRegistrations || []).map((reg, idx) => ({
          id: reg.id,
          letter: String.fromCharCode(65 + idx),
          player1: reg.player1_name || 'Por asignar',
          player2: reg.player2_name,
          isUser: reg.user_id === userId || reg.partner_user_id === userId
        }));
        console.log('[useUserGroupInfo] Fallback: built participants from registration order (no group_calendar)', {
          group: userGroup, category: fullCategory
        });
      }

      // Fallback: try tournament_schedules.groupBlocks
      try {
        const { data: scheduleData } = await supabase
          .from('tournament_schedules')
          .select('schedule_data')
          .eq('tournament_id', eventId)
          .single();

        if (scheduleData?.schedule_data) {
          const sd = scheduleData.schedule_data as any;
          const globalHideFb = sd._meta?.hideCourtsFromParticipants === true;
          const courtOverridesFb: Record<string, string> = sd.pickleballCourtOverrides || {};
          const catPartsFb = fullCategory.split(' ');
          const blockSubcategoryFb = catPartsFb.slice(1).join(' ');

          const normalizeTimeFb = (t: string | undefined | null): string | null => {
            if (!t) return null;
            let c = t.trim();
            if (c.toLowerCase().includes('varios') || c.length < 3) return null;
            if (/^\d{1,2}:\d{2}:\d{2}$/.test(c)) c = c.slice(0, 5);
            if (/^\d:\d{2}$/.test(c)) c = '0' + c;
            return /^\d{2}:\d{2}$/.test(c) ? c : null;
          };

          for (const [dateKey, dayData] of Object.entries(sd)) {
            if (dateKey === '_meta' || !dayData) continue;
            const blocks = (dayData as any).groupBlocks || [];
            const matchingBlock = blocks.find((b: any) => {
              const bGroup = (b.group || '').trim().replace(/\s+/g, ' ');
              const bCategory = (b.category || '').trim().replace(/\s+/g, ' ');
              return bGroup === userGroup && bCategory === fullCategory;
            });

            if (matchingBlock) {
              // Resolve court: check pickleballCourtOverrides first, then block.court
              const fbMatchTimes = ((matchingBlock.matches || []) as any[])
                .map((m: any) => normalizeTimeFb(m.time))
                .filter((t): t is string => !!t)
                .sort();
              const fbDerivedStart = fbMatchTimes.length > 0
                ? fbMatchTimes[0]
                : (normalizeTimeFb(matchingBlock.startTime) || matchingBlock.startTime || '');
              const fbSig = [dateKey, fullCategory, blockSubcategoryFb, 'grupos', userGroup, fbDerivedStart]
                .join('||').toLowerCase();
              const fbOverrideCourt: string | undefined = courtOverridesFb[fbSig];
              const fbCourtHidden = effectiveCourtHidden(matchingBlock.courtHidden, globalHideFb);
              const fbResolvedCourt = fbCourtHidden
                ? undefined
                : (fbOverrideCourt || (matchingBlock.court ? `Cancha ${matchingBlock.court}` : undefined));

              // Build map: organizer schedule letter (A,B,C from teamOrder) -> real participant letter
              const orgLetterToParticipantLetter = new Map<string, string>();
              if (teamsOrder && teamsOrder.length > 0) {
                const sortedTeams = [...teamsOrder].sort(
                  (a: any, b: any) => (a.teamOrder || 0) - (b.teamOrder || 0)
                );
                sortedTeams.forEach((team: any, tIdx: number) => {
                  const orgLetter = String.fromCharCode(65 + tIdx);
                  const matchingParticipant = participants.find(p =>
                    p.id === team.registrationId ||
                    normName(`${p.player1}${p.player2 ? ' / ' + p.player2 : ''}`) === normName(team.name || '')
                  );
                  if (matchingParticipant) {
                    orgLetterToParticipantLetter.set(orgLetter, matchingParticipant.letter);
                  }
                });
              }

              const matches: GroupMatch[] = (matchingBlock.matches || []).map((m: any, idx: number) => {
                const matchStr = m.match || '';
                const parts = matchStr.split('-');
                const teamCodes = parts[parts.length - 1] || '';
                const orgP1 = teamCodes.length >= 2 ? teamCodes[0] : '';
                const orgP2 = teamCodes.length >= 2 ? teamCodes[1] : '';
                const p1 = orgLetterToParticipantLetter.get(orgP1) || orgP1;
                const p2 = orgLetterToParticipantLetter.get(orgP2) || orgP2;
                return {
                  id: m.id || `${userGroup}-${idx}`,
                  matchId: matchStr,
                  participants: [p1, p2] as [string, string],
                  time: m.time || '',
                  date: dateKey,
                  court: fbResolvedCourt
                };
              });

              console.log('[useUserGroupInfo] Fallback: found groupBlock in tournament_schedules', {
                group: userGroup, category: fullCategory, matchCount: matches.length,
                fbSig, fbOverrideCourt, blockCourt: matchingBlock.court, fbResolvedCourt
              });

              return {
                groupName: userGroup,
                category: userCategory || '',
                subcategory: userSubcategory ?? undefined,
                date: dateKey,
                startTime: matchingBlock.startTime || '',
                endTime: matchingBlock.endTime || '',
                court: fbResolvedCourt,
                courtHidden: fbCourtHidden,
                participants,
                matches
              };
            }
          }
        }
      } catch (fallbackErr) {
        console.warn('[useUserGroupInfo] Fallback to tournament_schedules failed:', fallbackErr);
      }

      // No schedule data found anywhere
      return {
        groupName: userGroup,
        category: userCategory || '',
        subcategory: userSubcategory,
        date: '',
        startTime: '',
        endTime: '',
        participants,
        matches: []
      };
    }

    // Parse group calendar data
    const parsedInfo = parseGroupScheduleData(
      groupCalendar as any,
      userId,
      groupRegistrations || []
    );

    parsedInfo.category = userCategory || '';
    parsedInfo.subcategory = userSubcategory ?? undefined;

    // Enrich with court from tournament_schedules:
    // 1) prefer pickleballCourtOverrides (organizer's manual pre-assignment)
    // 2) fall back to groupBlocks[].court (original scheduled court)
    try {
      const { data: scheduleData } = await supabase
        .from('tournament_schedules')
        .select('schedule_data')
        .eq('tournament_id', eventId)
        .single();

      if (scheduleData?.schedule_data) {
        const sd = scheduleData.schedule_data as any;
        const globalHide = sd._meta?.hideCourtsFromParticipants === true;
        // Top-level overrides saved by organizer via "Cambiar cancha"
        const courtOverrides: Record<string, string> = sd.pickleballCourtOverrides || {};
        const catParts = fullCategory.split(' ');
        const blockSubcategory = catParts.slice(1).join(' ');

        // Mirror the organizer's normalizeTimeValue so our signature matches exactly
        const normalizeTime = (t: string | undefined | null): string | null => {
          if (!t) return null;
          let c = t.trim();
          if (c.toLowerCase().includes('varios') || c.length < 3) return null;
          if (/^\d{1,2}:\d{2}:\d{2}$/.test(c)) c = c.slice(0, 5);
          if (/^\d:\d{2}$/.test(c)) c = '0' + c;
          return /^\d{2}:\d{2}$/.test(c) ? c : null;
        };

        for (const [dateKey, dayData] of Object.entries(sd)) {
          if (dateKey === '_meta' || !dayData || typeof dayData !== 'object' || Array.isArray(dayData)) continue;
          const blocks = (dayData as any).groupBlocks || [];
          const matchingBlock = blocks.find((b: any) => {
            const bGroup = (b.group || '').trim().replace(/\s+/g, ' ');
            const bCategory = (b.category || '').trim().replace(/\s+/g, ' ');
            return bGroup === userGroup && bCategory === fullCategory;
          });
          if (!matchingBlock) continue;

          // Mirror organizer's derivedStartTime: earliest normalised match time, or block.startTime
          const matchTimes = ((matchingBlock.matches || []) as any[])
            .map((m: any) => normalizeTime(m.time))
            .filter((t): t is string => !!t)
            .sort();
          const derivedStart = matchTimes.length > 0
            ? matchTimes[0]
            : (normalizeTime(matchingBlock.startTime) || (matchingBlock.startTime || ''));

          // Build the same stable signature the organizer uses for pickleballCourtOverrides
          const sig = [dateKey, fullCategory, blockSubcategory, 'grupos', userGroup, derivedStart]
            .join('||').toLowerCase();
          const overrideCourt: string | undefined = courtOverrides[sig];

          console.log('[useUserGroupInfo] Court sig:', sig, '→ override:', overrideCourt, '| block.court:', matchingBlock.court, '| overrides keys:', Object.keys(courtOverrides));

          const courtHidden = effectiveCourtHidden(matchingBlock.courtHidden, globalHide);
          const resolvedCourt = courtHidden
            ? undefined
            : (overrideCourt || (matchingBlock.court ? `Cancha ${matchingBlock.court}` : undefined));

          if (courtHidden) {
            // Ocultar cancha preasignada: el participante verá "Por definir"
            parsedInfo.courtHidden = true;
            parsedInfo.court = undefined;
            parsedInfo.matches = parsedInfo.matches.map(m => ({ ...m, court: undefined }));
          } else if (resolvedCourt) {
            parsedInfo.court = resolvedCourt;
            parsedInfo.matches = parsedInfo.matches.map(m => ({ ...m, court: resolvedCourt }));
          }
          break;
        }
      }
    } catch (_e) { /* ignore */ }

    return parsedInfo;
  };

  const fetchGroupInfo = async () => {
    if (!eventId || typeof eventId !== 'string' || eventId.length < 10) {
      console.error('[useUserGroupInfo] Invalid eventId:', { eventId });
      setError('Invalid event ID');
      setLoading(false);
      return;
    }

    if (!user || !eventId) {
      setLoading(false);
      return;
    }

    console.log('[useUserGroupInfo] === STARTING FETCH ===', { eventId, userId: user.id });

    try {
      setLoading(true);
      setError(null);

      // Verify user profile
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('user_id, full_name, email')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError || !userProfile) {
        console.error('[useUserGroupInfo] Usuario sin perfil válido:', { userId: user.id });
        setError('Perfil de usuario no encontrado.');
        setLoading(false);
        return;
      }

      // 1. Get ALL user registrations using a single OR query (covers both user_id AND partner_user_id)
      let { data: userRegistrations, error: regError } = await supabase
        .from('registrations')
        .select('user_id, partner_user_id, group, category, subcategory, player1_name, player2_name, registered_at')
        .eq('event_id', eventId)
        .eq('status', 'confirmed')
        .or(`user_id.eq.${user.id},partner_user_id.eq.${user.id}`)
        .order('registered_at', { ascending: true });

      console.log('[useUserGroupInfo] Combined OR query:', {
        count: userRegistrations?.length || 0,
        regs: (userRegistrations || []).map(r => ({
          group: r.group,
          category: r.category,
          subcategory: r.subcategory,
          isUserPlayer1: r.user_id === user.id,
          isUserPlayer2: r.partner_user_id === user.id
        }))
      });

      // Fallback: search by name
      if ((!userRegistrations || userRegistrations.length === 0) && !regError && userProfile.full_name) {
        console.warn('[useUserGroupInfo] Fallback: searching by name:', userProfile.full_name);
        
        const { data: nameRegistrations, error: nameError } = await supabase
          .from('registrations')
          .select('user_id, partner_user_id, group, category, subcategory, player1_name, player2_name, registered_at')
          .eq('event_id', eventId)
          .eq('status', 'confirmed')
          .or(`player1_name.ilike.%${userProfile.full_name}%,player2_name.ilike.%${userProfile.full_name}%`);

        if (nameRegistrations && nameRegistrations.length > 0) {
          userRegistrations = nameRegistrations;
        }
        regError = nameError;
      }

      if (regError) throw regError;
      
      if (!userRegistrations || userRegistrations.length === 0) {
        setGroupInfo(null);
        setAllGroupInfos([]);
        setLoading(false);
        return;
      }

      console.log('[useUserGroupInfo] Total registrations found:', userRegistrations.length);

      // Process ALL registrations into GroupInfo[]
      const infos: GroupInfo[] = [];
      for (const reg of userRegistrations) {
        try {
          const info = await processRegistration(reg, user.id);
          if (info) {
            infos.push(info);
          }
        } catch (err) {
          console.error('[useUserGroupInfo] Error processing registration:', err);
        }
      }

      console.log('[useUserGroupInfo] ✅ Processed group infos:', infos.length);
      infos.forEach((info, idx) => {
        console.log(`[useUserGroupInfo] 📋 Info[${idx}]:`, {
          group: info.groupName,
          category: info.category,
          subcategory: info.subcategory,
          court: info.court,
          participants: info.participants.map(p => ({ letter: p.letter, name: `${p.player1}${p.player2 ? '/' + p.player2 : ''}`, isUser: p.isUser })),
          matches: info.matches.map(m => ({ id: m.id, time: m.time, participants: m.participants, court: m.court }))
        });
      });
      
      setAllGroupInfos(infos);
      setGroupInfo(infos[0] || null);

      // Update user groups set for realtime filtering
      const keys = new Set<string>();
      for (const reg of userRegistrations) {
        const cat = reg.subcategory
          ? `${(reg.category || '').trim()} ${reg.subcategory.trim()}`.trim()
          : (reg.category || '').trim();
        keys.add(normalizeKey(reg.group, cat));
      }
      userGroupsRef.current = keys;
    } catch (err) {
      console.error('Error fetching group info:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar información del grupo');
    } finally {
      setLoading(false);
    }
  };

  const isUserAffectedByCalendarPayload = (payload: any): boolean => {
    const row = payload?.new || payload?.old;
    if (!row) return false;
    const key = normalizeKey(row.group_name, row.category);
    return userGroupsRef.current.has(key);
  };

  const isUserAffectedByScheduleUpdate = (payload: any): boolean => {
    const sd = payload?.new?.schedule_data;
    if (!sd) return false;
    try {
      // Extract only the groupBlocks that match the user's groups
      const userBlocks: any[] = [];
      for (const [dateKey, dayData] of Object.entries(sd as any)) {
        if (dateKey === '_meta' || !dayData) continue;
        const blocks = (dayData as any).groupBlocks || [];
        for (const b of blocks) {
          const cat = (b.category || '').trim().replace(/\s+/g, ' ');
          const key = normalizeKey(b.group, cat);
          if (userGroupsRef.current.has(key)) {
            userBlocks.push({ d: dateKey, g: b.group, c: cat, court: b.court, st: b.startTime, et: b.endTime, m: b.matches });
          }
        }
      }
      if (userBlocks.length === 0) return false;
      const snapshot = JSON.stringify(userBlocks);
      if (snapshot === lastScheduleSnapshotRef.current) return false;
      lastScheduleSnapshotRef.current = snapshot;
      return true;
    } catch {
      return true;
    }
  };

  useEffect(() => {
    if (!eventId || !user?.id) return;

    fetchGroupInfo();

    // Realtime: refetch when organizer changes group_calendars or tournament_schedules
    const channel = supabase
      .channel(`group-info-${eventId}-${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'group_calendars', filter: `tournament_id=eq.${eventId}` },
        (payload) => {
          if (!isUserAffectedByCalendarPayload(payload)) {
            console.log('[useUserGroupInfo] Ignored group_calendars change (other group)');
            return;
          }
          fetchGroupInfo();
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tournament_schedules', filter: `tournament_id=eq.${eventId}` },
        (payload) => {
          if (!isUserAffectedByScheduleUpdate(payload)) {
            console.log('[useUserGroupInfo] Ignored tournament_schedules change (no user-block diff)');
            return;
          }
          fetchGroupInfo();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [eventId, user?.id]);

  return {
    groupInfo,
    allGroupInfos,
    loading,
    error,
    refetch: fetchGroupInfo
  };
}
